import { doc, getDoc, getDocFromServer } from 'firebase/firestore';
import { db, auth } from '../../../config/firebase';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { organizeElementsBySpread } from '../../../utils/spreadElements';
import { hasResourceAccess } from '../../../utils/adminUtils';

/**
 * @param {string} bookId
 * @returns {Promise<Record<string, any>>} The book document with id, pdfUrls, spreads, etc.
 */
export async function getBook(bookId) {
  try {
    const userId = auth.currentUser?.uid;
    if (!userId) throw new Error('User must be logged in');

    console.log('🔍 getBook: Attempting to fetch book with ID:', bookId);
    console.log('🔍 getBook: Current user ID:', userId);

    const bookRef = doc(db, 'books', bookId);
    let bookSnap;
    try {
      // Prefer server read first to avoid stale cache returning non-existence
      bookSnap = await getDocFromServer(bookRef);
      console.log('🔍 this is it getBook: Fetched from server. exists():', bookSnap.exists());
    } catch (e) {
      console.warn('⚠️ this is it getBook: getDocFromServer failed, falling back to cache/online getDoc()', e?.message);
      bookSnap = await getDoc(bookRef);
      console.log('🔍 this is it getBook: Fallback getDoc. exists():', bookSnap.exists());
    }

    if (!bookSnap.exists()) {
      console.error('❌ getBook: Book document does not exist in Firestore for ID:', bookId);
      throw new Error('Book not found');
    }

    const bookData = bookSnap.data();
    console.log('Book data:', bookData);

    const hasAccess = await hasResourceAccess(auth.currentUser, bookData.userId);
    if (!hasAccess) {
      throw new Error('Unauthorized access');
    }

    // Ensure pdfUrls is properly initialized if it doesn't exist
    const pdfUrls = bookData.pdfUrls || { 
      cover: null, 
      verticalContent: null, 
      horizontalContent: null 
    };

    // If we already have spreads data in Firebase, use that directly
    if (bookData.spreads) {
      console.log('Using existing spreads data from Firebase');
      console.log('🔍 getBook: Spreads data structure:', {
        spreadIds: Object.keys(bookData.spreads),
        spreadDetails: Object.entries(bookData.spreads).map(([spreadId, spread]) => ({
          spreadId,
          elementCount: spread?.elements?.length || 0,
          elements: spread?.elements?.map(el => ({
            id: el.id,
            type: el.type,
            orientation: el.orientation,
            orientationType: typeof el.orientation,
          })) || []
        }))
      });
      return {
        id: bookSnap.id,
        ...bookData,
        pdfUrls, // Ensure pdfUrls is included
      };
    }

    // Otherwise, fall back to the old method of organizing elements
    console.log('No spreads data found in Firebase, falling back to elements');

    // Get book elements - Inline the getBookElements functionality
    const elementsQuery = query(
      collection(db, 'elements'),
      where('bookId', '==', bookId),
      where('userId', '==', userId)
    );

    const snapshot = await getDocs(elementsQuery);
    const elements = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    console.log('Loaded book elements:', elements);

    // Organize elements by spread
    const spreads = organizeElementsBySpread(elements);
    console.log('Organized spreads:', spreads);

    // Initialize missing spreads
    const totalSpreads = Math.ceil(bookData.totalPages / 2);
    const initializedSpreads = {
      'spread-0': spreads['spread-0'] || { elements: [] },
    };

    // Create content spreads
    for (let i = 1; i < totalSpreads; i++) {
      initializedSpreads[`spread-${i}`] = spreads[`spread-${i}`] || {
        elements: [],
      };
    }

    return {
      id: bookSnap.id,
      ...bookData,
      pdfUrls, // Ensure pdfUrls is included
      spreads: initializedSpreads,
      elements, // Keep elements array for backward compatibility
    };
  } catch (error) {
    console.error('Error getting book:', error);
    throw error;
  }
}
